<template>
  <div v-if="hasBackupCareRequests">
    <BackupCareRequestSelection :scCareSessions="scCareSessions" @careTypeSelected="updateCareType" />
  </div>
  <div v-else>
    <CareTypeCardGroup
      v-if="!isLoading"
      :careTypes="careTypes"
      :headingText="'What type of care do you currently need?'"
      @select="saveCareType"
    />
  </div>
</template>

<script lang="ts">
import { sendBrazeEvent } from '../../../lib/braze';
import { redirect } from '@/helpers/redirects';
import { CareType } from '@/types/care_type';
import CareTypeCardGroup from '@/components/careType/CareTypeCardGroup.vue';
import { sendSegmentEvent } from '../../../lib/segment';
import OnboardingMutations from '../../../graphql/onboarding/mutations';
import { SC_CARE_SESSIONS_QUERY } from '@/graphql/backup_care/queries';
import { BASIC_REGISTRATION_CARE_TYPE_LOADED } from '@/lib/constants/braze_custom_events';
import BackupCareRequestSelection from './BackupCareRequestSelection.vue';

export default {
  name: 'CareTypeForm',
  components: {
    CareTypeCardGroup,
    BackupCareRequestSelection,
  },
  props: {
    useNewJobPost: {
      type: Boolean,
      default: undefined,
    },
    isCorporate: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isLoading: false,
      selectedCareType: '',
      hasBackupCareRequests: false,
      bhReservationRequests: [],
    };
  },
  computed: {
    corporateCareTypeTitles(): string[] {
      return ['CHILD_CARE', 'COMPANION_CARE', 'PET_CARE', 'SPECIAL_NEEDS', 'HOUSEKEEPING'];
    },
    parentCareTypeTitles(): string[] {
      return ['CHILD_CARE', 'COMPANION_CARE', 'PET_CARE', 'SPECIAL_NEEDS'];
    },
    careTypes(): string[] {
      return this.isCorporate ? this.corporateCareTypeTitles : this.parentCareTypeTitles;
    },
    scCareSessions() {
      return this.bhReservationRequests.flatMap((session: any) => session.scCareSessions);
    },
  },
  async beforeMount() {
    if (this.isCorporate) {
      await this.checkForBackupCareRequests();
    }
  },
  mounted() {
    sendBrazeEvent(BASIC_REGISTRATION_CARE_TYPE_LOADED);
  },
  methods: {
    updateCareType(careType: CareType) {
      this.selectedCareType = careType;
    },
    async saveCareType(careType: CareType | 'CHILD_CARE') {
      this.selectedCareType = careType;
      try {
        await this.$apollo.mutate({
          mutation: OnboardingMutations.COMPLETE_CARE_TYPE_TASK,
          // default to babysitter until CHILD_CARE is implemented on the backend mutation
          variables: { careType: this.selectedCareType === 'CHILD_CARE' ? 'BABYSITTER' : this.selectedCareType },
        });
        if (!this.hasBackupCareRequests) {
          this.handlePostCareTypeSelection();
        }
      } catch (error) {
        this.handleError();
      }
    },
    handlePostCareTypeSelection() {
      this.sendCareTypeSelectionEvent();
      if (this.isCorporate) {
        this.$emit('onComplete');
      } else {
        this.redirectAfterSelection();
      }
    },
    async checkForBackupCareRequests() {
      this.isLoading = true;
      try {
        const { data } = await this.$apollo.query({
          query: SC_CARE_SESSIONS_QUERY,
        });
        this.bhReservationRequests = data.corp.backupCare.bhReservationRequests || [];
        this.hasBackupCareRequests = this.scCareSessions.length > 0;
        if (this.hasBackupCareRequests) {
          this.autoSetCareTypeForBUCReservations();
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        this.isLoading = false;
      }
    },
    autoSetCareTypeForBUCReservations(): void {
      const babysitterCareType: boolean = this.scCareSessions.some((session) =>
        [CareType.BABYSITTER].includes(session?.careType)
      );
      const companionCareType: boolean = this.scCareSessions.some((session) =>
        [CareType.COMPANION_CARE].includes(session?.careType)
      );

      // default to babysitter care type if both are present or only babysitter is present
      let careType: CareType = CareType.BABYSITTER;
      if (!babysitterCareType && companionCareType) {
        // Set the care type to companion care if only companion care is present
        careType = CareType.COMPANION_CARE;
      }

      if (careType) {
        this.saveCareType(careType);
        this.sendCareTypeSelectionEvent();
      }
    },
    sendCareTypeSelectionEvent() {
      sendSegmentEvent('Care Type Selected', { care_type: this.selectedCareType });
    },
    redirectAfterSelection() {
      const path = this.selectedCareType === 'CHILD_CARE' ? `/job-post?source=registration` : '/registration';
      redirect(path);
    },
    handleError() {
      redirect('/something-went-wrong?retry=true');
    },
  },
};
</script>
